'use client';

import React, { useState, useMemo } from 'react';
import { ArrowLeft, Search, Users, UserPlus, X, Check } from 'lucide-react';

interface Contact {
  id: string;
  name: string;
  phone: string;
  initials: string;
  avatarGradient: string;
  isOnline?: boolean;
  isGroup?: boolean;
  memberCount?: number;
}

const ALL_CONTACTS: Contact[] = [
  {
    id: 'c1',
    name: 'Ate Boni',
    phone: '+63 912 345 6789',
    initials: 'AB',
    avatarGradient: 'linear-gradient(135deg, #287FD8 0%, #6650D8 100%)',
    isOnline: true,
  },
  {
    id: 'c2',
    name: 'Marco Reyes',
    phone: '+63 917 234 5678',
    initials: 'MR',
    avatarGradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
    isOnline: true,
  },
  {
    id: 'c3',
    name: 'Ana Santos',
    phone: '+63 918 876 5432',
    initials: 'AS',
    avatarGradient: 'linear-gradient(135deg, #06B6D4 0%, #0284C7 100%)',
    isOnline: true,
  },
  {
    id: 'c4',
    name: 'Kuya Dan',
    phone: '+63 920 111 2233',
    initials: 'KD',
    avatarGradient: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
  },
  {
    id: 'c5',
    name: 'Paolo Cruz',
    phone: '+63 915 999 0011',
    initials: 'PC',
    avatarGradient: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)',
  },
  {
    id: 'c6',
    name: 'Lola Nena',
    phone: '+63 919 444 5566',
    initials: 'LN',
    avatarGradient: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
  },
  {
    id: 'c7',
    name: 'Carlo Mendoza',
    phone: '+63 916 777 8899',
    initials: 'CM',
    avatarGradient: 'linear-gradient(135deg, #EC4899 0%, #BE185D 100%)',
    isOnline: true,
  },
  {
    id: 'c8',
    name: 'Jess Villanueva',
    phone: '+63 921 333 4455',
    initials: 'JV',
    avatarGradient: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
  },
  {
    id: 'g1',
    name: 'Design Team',
    phone: '',
    initials: 'DT',
    avatarGradient: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
    isGroup: true,
    memberCount: 8,
  },
  {
    id: 'g2',
    name: 'Jess & Friends',
    phone: '',
    initials: 'JF',
    avatarGradient: 'linear-gradient(135deg, #EC4899 0%, #BE185D 100%)',
    isGroup: true,
    memberCount: 14,
  },
  {
    id: 'g3',
    name: 'Barkada GC',
    phone: '',
    initials: 'BG',
    avatarGradient: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
    isGroup: true,
    memberCount: 22,
  },
  {
    id: 'g4',
    name: 'Work Updates',
    phone: '',
    initials: 'WU',
    avatarGradient: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
    isGroup: true,
    memberCount: 5,
  },
];

type Tab = 'all' | 'contacts' | 'groups';

interface NewConversationScreenProps {
  onBack: () => void;
  onStartChat: () => void;
}

export default function NewConversationScreen({ onBack, onStartChat }: NewConversationScreenProps) {
  const [query, setQuery] = useState('');
  const [activeTab, setActiveTab] = useState<Tab>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isGroupMode, setIsGroupMode] = useState(false);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return ALL_CONTACTS.filter(c => {
      const matchesQuery =
        !q || c.name.toLowerCase().includes(q) || c.phone.includes(q);
      const matchesTab =
        activeTab === 'all' ||
        (activeTab === 'contacts' && !c.isGroup) ||
        (activeTab === 'groups' && c.isGroup);
      return matchesQuery && matchesTab;
    });
  }, [query, activeTab]);

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleStartGroupMode = () => {
    setIsGroupMode(true);
    setActiveTab('contacts');
    setSelectedIds(new Set());
  };

  const handleCancelGroup = () => {
    setIsGroupMode(false);
    setActiveTab('all');
    setSelectedIds(new Set());
  };

  const canProceed = isGroupMode ? selectedIds.size >= 2 : selectedIds.size === 1;

  return (
    <div
      style={{
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: '#101C29',
      }}
    >
      {/* Header */}
      <header
        className="flex items-center gap-3 px-3 flex-shrink-0"
        style={{
          height: '64px',
          background: 'rgba(16,28,41,0.97)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <button
          onClick={isGroupMode ? handleCancelGroup : onBack}
          className="flex items-center justify-center w-[40px] h-[40px] rounded-full text-white/70 hover:bg-white/10 hover:text-white transition-all duration-150 flex-shrink-0"
          aria-label="Back"
        >
          <ArrowLeft size={22} />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-[18px] font-bold text-white tracking-tight leading-tight">
            {isGroupMode ? 'New Group' : 'New Conversation'}
          </h1>
          {isGroupMode && selectedIds.size > 0 && (
            <p className="text-[12px]" style={{ color: '#4A9EE8' }}>
              {selectedIds.size} selected
            </p>
          )}
        </div>
        {isGroupMode && canProceed && (
          <button
            onClick={onStartChat}
            className="flex items-center justify-center w-[40px] h-[40px] rounded-full transition-all duration-150"
            style={{ background: '#287FD8' }}
            aria-label="Create group"
          >
            <Check size={20} className="text-white" />
          </button>
        )}
      </header>

      {/* Quick Actions */}
      {!isGroupMode && (
        <div
          className="flex gap-3 px-4 py-3 flex-shrink-0"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
        >
          <QuickActionButton
            icon={<UserPlus size={20} />}
            label="New Contact"
            gradient="linear-gradient(135deg, #287FD8 0%, #1A5FAA 100%)"
            onClick={onStartChat}
          />
          <QuickActionButton
            icon={<Users size={20} />}
            label="New Group"
            gradient="linear-gradient(135deg, #22C55E 0%, #15803D 100%)"
            onClick={handleStartGroupMode}
          />
        </div>
      )}

      {/* Selected chips (group mode) */}
      {isGroupMode && selectedIds.size > 0 && (
        <div
          className="flex gap-2 px-4 py-2.5 overflow-x-auto flex-shrink-0"
          style={{
            borderBottom: '1px solid rgba(255,255,255,0.06)',
            scrollbarWidth: 'none',
          }}
        >
          {Array.from(selectedIds).map(id => {
            const contact = ALL_CONTACTS.find(c => c.id === id);
            if (!contact) return null;
            return (
              <button
                key={id}
                onClick={() => toggleSelect(id)}
                className="flex items-center gap-1.5 px-3 py-1 rounded-full flex-shrink-0 transition-all duration-150"
                style={{ background: 'rgba(40,127,216,0.25)', border: '1px solid rgba(40,127,216,0.5)' }}
              >
                <span className="text-[13px] font-medium text-white">{contact.name.split(' ')[0]}</span>
                <X size={13} className="text-white/60" />
              </button>
            );
          })}
        </div>
      )}

      {/* Search bar */}
      <div className="px-4 py-3 flex-shrink-0">
        <div
          className="flex items-center gap-2.5 px-3.5 rounded-2xl"
          style={{ background: 'rgba(255,255,255,0.07)', height: '42px' }}
        >
          <Search size={16} className="text-white/40 flex-shrink-0" />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search by name or phone…"
            className="flex-1 bg-transparent text-white text-[14px] outline-none placeholder:text-white/35"
            autoComplete="off"
          />
          {query.length > 0 && (
            <button onClick={() => setQuery('')} className="text-white/40 hover:text-white/70 transition-colors">
              <X size={15} />
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      {!isGroupMode && (
        <div
          className="flex px-4 gap-2 pb-3 flex-shrink-0"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
        >
          {(['all', 'contacts', 'groups'] as Tab[]).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className="px-4 py-1.5 rounded-full text-[13px] font-medium transition-all duration-150 capitalize"
              style={
                activeTab === tab
                  ? { background: '#287FD8', color: '#fff' }
                  : { background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.55)' }
              }
            >
              {tab}
            </button>
          ))}
        </div>
      )}

      {/* Contact list */}
      <div className="flex-1 overflow-y-auto" style={{ overscrollBehavior: 'contain' }}>
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 px-8">
            <div
              className="w-[60px] h-[60px] rounded-full flex items-center justify-center"
              style={{ background: 'rgba(255,255,255,0.06)' }}
            >
              <Search size={26} className="text-white/30" />
            </div>
            <p className="text-[15px] font-medium text-white/50 text-center">No results found</p>
            <p className="text-[13px] text-white/30 text-center">
              Try a different name or phone number
            </p>
          </div>
        ) : (
          filtered.map((contact, idx) => (
            <ContactRow
              key={contact.id}
              contact={contact}
              isLast={idx === filtered.length - 1}
              isGroupMode={isGroupMode}
              isSelected={selectedIds.has(contact.id)}
              onSelect={() => {
                if (isGroupMode) {
                  toggleSelect(contact.id);
                } else {
                  onStartChat();
                }
              }}
            />
          ))
        )}
      </div>

      {/* Bottom CTA for group mode */}
      {isGroupMode && canProceed && (
        <div
          className="px-4 py-4 flex-shrink-0"
          style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
        >
          <button
            onClick={onStartChat}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl text-white font-semibold text-[15px] transition-all duration-150 active:scale-[0.98]"
            style={{ background: 'linear-gradient(135deg, #287FD8 0%, #1A5FAA 100%)' }}
          >
            <Users size={18} />
            Create Group ({selectedIds.size} members)
          </button>
        </div>
      )}
    </div>
  );
}

function QuickActionButton({
  icon,
  label,
  gradient,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  gradient: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="flex-1 flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-150 active:scale-[0.97]"
      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
    >
      <div
        className="w-[38px] h-[38px] rounded-full flex items-center justify-center flex-shrink-0"
        style={{ background: gradient }}
      >
        <span className="text-white">{icon}</span>
      </div>
      <span className="text-[14px] font-semibold text-white/90">{label}</span>
    </button>
  );
}

function ContactRow({
  contact,
  isLast,
  isGroupMode,
  isSelected,
  onSelect,
}: {
  contact: Contact;
  isLast: boolean;
  isGroupMode: boolean;
  isSelected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      onClick={onSelect}
      className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors duration-100"
      style={{
        borderBottom: isLast ? 'none' : '1px solid rgba(255,255,255,0.04)',
        background: isSelected ? 'rgba(40,127,216,0.12)' : 'transparent',
      }}
      onMouseEnter={e => {
        if (!isSelected)
          (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.04)';
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLButtonElement).style.background = isSelected
          ? 'rgba(40,127,216,0.12)'
          : 'transparent';
      }}
    >
      {/* Avatar */}
      <div className="relative flex-shrink-0">
        <div
          className="w-[50px] h-[50px] rounded-full flex items-center justify-center text-white font-bold text-[15px]"
          style={{ background: contact.avatarGradient }}
          aria-label={`${contact.name} avatar`}
        >
          {contact.initials}
        </div>
        {contact.isOnline && !contact.isGroup && (
          <span
            className="absolute bottom-[1px] right-[1px] w-[12px] h-[12px] bg-green-400 rounded-full border-2"
            style={{ borderColor: '#101C29' }}
            aria-label="Online"
          />
        )}
        {contact.isGroup && (
          <span
            className="absolute bottom-[-2px] right-[-2px] w-[18px] h-[18px] rounded-full flex items-center justify-center"
            style={{ background: '#287FD8', border: '2px solid #101C29' }}
          >
            <Users size={9} className="text-white" />
          </span>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-[15px] font-semibold text-white/92 truncate">{contact.name}</p>
        <p className="text-[12px] truncate mt-[2px]" style={{ color: 'rgba(255,255,255,0.38)' }}>
          {contact.isGroup
            ? `${contact.memberCount} members`
            : contact.isOnline
            ? 'Online'
            : contact.phone}
        </p>
      </div>

      {/* Selection indicator (group mode) */}
      {isGroupMode && (
        <div
          className="w-[24px] h-[24px] rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-150"
          style={
            isSelected
              ? { background: '#287FD8' }
              : { border: '2px solid rgba(255,255,255,0.25)', background: 'transparent' }
          }
        >
          {isSelected && <Check size={13} className="text-white" />}
        </div>
      )}
    </button>
  );
}
